import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, ShoppingBasket, Truck, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { THUISBEZORGD_ORDER_URL } from '@/components/OrderModal';

const Features = () => {
  const { t } = useLanguage();

  const featuresData = [
    {
      icon: <Leaf className="h-8 w-8 brand-text" />,
      title: t('features.feature1Title'),
      description: t('features.feature1Desc'),
      link: '/contact',
      linkLabel: t('features.feature1Link')
    },
    {
      icon: <ShoppingBasket className="h-8 w-8 brand-text" />,
      title: t('features.feature2Title'),
      description: t('features.feature2Desc'),
      link: '/market-day',
      linkLabel: t('features.feature2Link')
    },
    {
      icon: <Truck className="h-8 w-8 brand-text" />,
      title: t('features.feature3Title'),
      description: t('features.feature3Desc'),
      link: THUISBEZORGD_ORDER_URL,
      linkLabel: t('features.feature3Link')
    },
    {
      icon: <Users className="h-8 w-8 brand-text" />,
      title: t('features.feature4Title'),
      description: t('features.feature4Desc'),
      link: '/catering',
      linkLabel: t('features.feature4Link')
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <section id="services" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.span
            className="inline-block px-4 py-1.5 mb-4 text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 rounded-full"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t('features.sectionTitle')}
          </motion.span>
          
          <motion.h2
            className="text-3xl md:text-4xl font-bold mb-6 text-gray-800"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {t('features.title')}
          </motion.h2>
          
          <motion.p
            className="text-lg text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {t('features.subtitle')}
          </motion.p>
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {featuresData.map((feature, index) => (
            <motion.div
              key={index}
              className="bg-card p-6 rounded-xl shadow-lg card-hover border border-gray-100 flex flex-col"
              variants={itemVariants}
            >
              <div className="mb-5 bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">{feature.title}</h3>
              <p className="text-muted-foreground mb-5 flex-grow">{feature.description}</p>
              {feature.link.startsWith('http') ? (
                <a href={feature.link} target="_blank" rel="noopener noreferrer">
                  <Button variant="link" className="text-primary p-0 hover:text-primary/80">
                    {feature.linkLabel} <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </a>
              ) : (
                <Link to={feature.link}>
                  <Button variant="link" className="text-primary p-0 hover:text-primary/80">
                    {feature.linkLabel} <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;